import React, {useContext, useMemo} from 'react'
import AppContext from '../appContext'
import InterpolationAlgorithmType from '../controllers/InterpolationAlgorithmType'
import {customUnits, unitCustom} from '../pages/program/CustomUnits'

export type AmendmentType = 'lime' | 'gypsum' | 'kFert' | 'pFert'
export type YearOption = 'need' | 'y1' | 'y2' | 'y3'

export interface SampleValue {
    label: string
    value: number | null
}

export interface ApplicationDataSidebarProps {
    sampleValues: SampleValue[]
    amendmentType: AmendmentType
    yearOption: YearOption
    algorithm: InterpolationAlgorithmType
    onAmendmentChange: (type: AmendmentType) => void
    onYearChange: (year: YearOption) => void
    onAlgorithmChange: (algorithm: InterpolationAlgorithmType) => void
    hoveredSampleLabel: string | null
    onSampleHover: (label: string) => void
    onSampleLeave: () => void
    selectedSampleLabel?: string | null
    onSampleClick?: (label: string) => void
}

const ApplicationDataSidebar: React.FC<ApplicationDataSidebarProps> = ({
    sampleValues,
    amendmentType,
    yearOption,
    algorithm,
    onAmendmentChange,
    onYearChange,
    onAlgorithmChange,
    hoveredSampleLabel,
    onSampleHover,
    onSampleLeave,
    selectedSampleLabel,
    onSampleClick
}) => {
    const app = useContext(AppContext)
    const massPerAreaUnit = unitCustom(app.initial.system, customUnits.kgha_lbAc)

    // Sort samples alphabetically by label
    const sortedSamples = useMemo(() =>
        [...sampleValues].sort((a, b) => a.label.localeCompare(b.label)),
        [sampleValues]
    )

    // Get available year options based on amendment type
    // Lime has Y3, others only have Y1 and Y2
    const yearOptions: YearOption[] = amendmentType === 'lime'
        ? ['need', 'y1', 'y2', 'y3']
        : ['need', 'y1', 'y2']

    // Format value for display
    const formatValue = (value: number | null): string => {
        if (value === null || value === undefined) return '-'
        return value.toLocaleString(undefined, {maximumFractionDigits: 0})
    }

    return (
        <div className="flex flex-col h-full bg-white border-l border-gray-200 w-56">
            {/* Selectors */}
            <div className="p-2 border-b border-gray-100 bg-gray-200">
                <div className="mb-2">
                    <label className="text-xs text-gray-600 block mb-1">{app.word('amendment_type')}</label>
                    <select
                        className="input text-xs w-full"
                        value={amendmentType}
                        onChange={e => onAmendmentChange(e.target.value as AmendmentType)}
                    >
                        <option value="lime">{app.word('lime')}</option>
                        <option value="gypsum">{app.word('gypsum')}</option>
                        <option value="kFert">{app.word('k_fertiliser')}</option>
                        <option value="pFert">{app.word('p_fertiliser')}</option>
                    </select>
                </div>
                <div>
                    <label className="text-xs text-gray-600 block mb-1">{app.word('year')}</label>
                    <select
                        className="input text-xs w-full"
                        value={yearOption}
                        onChange={e => onYearChange(e.target.value as YearOption)}
                    >
                        {yearOptions.map(year => (
                            <option key={year} value={year}>
                                {year === 'need' ? app.word('total_need') : year.toUpperCase()}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="mt-2">
                    <label className="text-xs text-gray-600 block mb-1">{app.word('interpolation')}</label>
                    <select
                        className="input text-xs w-full"
                        value={algorithm}
                        onChange={e => onAlgorithmChange(e.target.value as InterpolationAlgorithmType)}
                    >
                        {Object.values(InterpolationAlgorithmType).map(alg => (
                            <option key={alg} value={alg}>
                                {InterpolationAlgorithmType[alg]}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Sample list with values */}
            <div className="flex-1 overflow-y-auto">
                {sortedSamples.length > 0 ? (
                    <ul>
                        {sortedSamples.map((sample, index) => {
                            const isHovered = sample.label === hoveredSampleLabel
                            const isSelected = sample.label === selectedSampleLabel

                            return (
                                <li
                                    key={sample.label || index}
                                    className={`px-2 py-1 text-xs cursor-pointer border-b border-gray-100 flex justify-between ${
                                        isSelected
                                            ? 'bg-primary-200 text-gray-900 font-medium'
                                            : isHovered
                                                ? 'bg-primary-100 text-gray-900 font-medium'
                                                : 'text-gray-600 hover:bg-gray-200'
                                    }`}
                                    onMouseEnter={() => onSampleHover(sample.label)}
                                    onMouseLeave={onSampleLeave}
                                    onClick={() => onSampleClick?.(sample.label)}
                                >
                                    <span>{sample.label}</span>
                                    <span className="text-gray-500">{formatValue(sample.value)} {massPerAreaUnit}</span>
                                </li>
                            )
                        })}
                    </ul>
                ) : (
                    <div className="p-2 text-xs text-gray-400">
                        {app.word('no_data')}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ApplicationDataSidebar
